import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@1trading/react';
import { RefreshCw } from 'lucide-react';
import { StatusChip } from '@/components/ui/status-chip';
import { useMarketMakerBalances } from '@/hooks/queries/market-maker/use-market-maker';
import { type AccountBalance, formatBalance } from '@/services/api/market-maker';
import { PRIMARY_CHIP_CLASS } from './status';

interface AccountCardProps {
  account: AccountBalance;
}

const AccountCard = ({ account }: AccountCardProps) => {
  const hasBalances = account.balances.length > 0;
  const hasPositions = account.positions.length > 0;
  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium">{account.name}</CardTitle>
          {/* 'maker' → primary, anything else → secondary (legacy) */}
          {account.role === 'maker' ? (
            <StatusChip className={`px-2 py-0.5 text-xs ${PRIMARY_CHIP_CLASS}`}>{account.role}</StatusChip>
          ) : (
            <StatusChip className="px-2 py-0.5 text-xs" variant="muted">
              {account.role}
            </StatusChip>
          )}
        </div>
        <CardDescription>{account.markets.join(', ')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3">
        {hasBalances ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Currency</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead className="text-right">Locked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {account.balances.map((balance) => (
                <TableRow key={balance.currency}>
                  <TableCell>{balance.currency}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatBalance(balance.available)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatBalance(balance.locked)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
        {hasPositions ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm text-muted-foreground">Open Positions:</span>
            {account.positions.map((position) => (
              <div className="flex items-center gap-2" key={position.symbol}>
                <StatusChip className="px-1.5 py-0 text-xs" variant={position.direction === 'LONG' ? 'success' : 'destructive'}>
                  {position.direction}
                </StatusChip>
                <span className="text-sm">{position.symbol}</span>
                <span className="text-sm font-medium tabular-nums">{formatBalance(position.openVolume)}</span>
              </div>
            ))}
          </div>
        ) : null}
        {!(hasBalances || hasPositions) ? <span className="text-sm text-muted-foreground">No balance data available</span> : null}
      </CardContent>
    </Card>
  );
};
AccountCard.displayName = 'AccountCard';

// The balances endpoint is slow/expensive — NEVER polled and excluded from
// the header refresh; it only loads when the operator clicks this section's Refresh.
export const BalancesSection = () => {
  const balancesQuery = useMarketMakerBalances();
  const accounts = balancesQuery.data?.accounts;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">Account Balances</h2>
        <Button className="gap-1.5" disabled={balancesQuery.isFetching} onClick={() => balancesQuery.refetch()} size="sm" type="button" variant="outline">
          {balancesQuery.isFetching ? <Spinner className="size-3.5" /> : <RefreshCw className="size-3.5" />}
          Refresh
        </Button>
      </div>
      {accounts && accounts.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {accounts.map((account) => (
            <AccountCard account={account} key={account.id} />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">Click refresh to load account balances</CardContent>
        </Card>
      )}
    </section>
  );
};
BalancesSection.displayName = 'BalancesSection';
